import {useNavigate, useParams} from 'react-router-dom'
import {TextValidator, ValidatorForm} from "react-material-ui-form-validator"
import Button from "@mui/material/Button"
import Box from "@mui/material/Box"
import React, {useEffect, useRef, useState} from "react"
import {getFieldErrorParams, getInputValue} from "../../../utils"
import AdminContainer from "../../../components/admin/AdminContainer"
import {
  Autocomplete,
  Chip,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton
} from "@mui/material"
import MenuItem from "@mui/material/MenuItem"
import DeleteIcon from "@mui/icons-material/Delete"
import AddIcon from "@mui/icons-material/Add"
import {useGlobalContext} from "../../../contexts/global_context"
import {BACKEND_URL, BRAND_NAME} from "../../../data/config"
import Divider from "@mui/material/Divider"
import PackageProviderTable from "../../../components/admin/package/PackageProviderTable"
import {Helmet} from "react-helmet";
import Typography from "@mui/material/Typography";

const ProviderCreateUpdateAdminPage = () => {
  const navigate = useNavigate()
  let {uid} = useParams()
  let form = useRef()
  let {sendAjax} = useGlobalContext()
  let [fieldErrors, setFieldErrors] = useState({})
  let [categories, setCategories] = useState([])

  useEffect(() => {
    // get categories
    sendAjax({
      url: `${BACKEND_URL}/api/v1/pkg/admin/providers/`,
      method: 'get',
      data: {},
    }).then(r => {
      if (r.data.status_code === 200) {
        setCategories(r.data.data.results)
      }
    })

    // get package
    if (uid) {
      sendAjax({
        url: `${BACKEND_URL}/api/v1/pkg/admin/providers/${uid}/`,
        method: 'get',
        data: {},
      }).then((r) => {
        if (r.data.status_code === 200) {
          let provider = r.data.data
          setFormValues({
            title: provider.title,
            service_type: provider.service_type,
            provider_type: provider.provider_type,
            service_code: provider.service_code,
            min_quantity: provider.min_quantity,
            max_quantity: provider.max_quantity,
            social: provider.social,
            service_status: provider.service_status,
            test_status: provider.test_status, // Read-only field for display
            test_status_display: provider.test_status_display, // For display purposes
            index: provider.index,
            starts_after: provider.starts_after || '',
            speed: provider.speed || '',
            quality: provider.quality || '',
            stability: provider.stability || '',
            drop: provider.drop || '',
            description: provider.description || '',
            auto_test_enabled: provider.auto_test_enabled || false,
            test_user_link: provider.test_user_link || '',
            test_post_link: provider.test_post_link || '',
            test_every: provider.test_every || '',
            complete_time: provider.complete_time || '',
            test_quantity: provider.test_quantity || '',
            test_parameters: provider.test_parameters || [],
          })
        }
      })
    } else {
      handleAddTestLink()
    }
  }, [])

  let [formValues, setFormValues] = useState({
    title: '',
    service_type: '',
    provider_type: '',
    service_code: '',
    min_quantity: '',
    max_quantity: '',
    social: '',
    service_status: '',
    test_status: '',
    test_status_display: '',
    index: '',
    starts_after: '',
    speed: '',
    quality: '',
    stability: '',
    drop: '',
    description: '',
    auto_test_enabled: false,
    test_user_link: '',
    test_post_link: '',
    test_every: '',
    complete_time: '',
    test_quantity: '',
    test_parameters: [],
  })

  const afterSubmit = (r) => {
    if (r.data.status_code === 200) {
      navigate("/admin/providers/")
    } else if (r.data.status_code === 400) {
      setFieldErrors(r.data.data)
    }
  }

  const handleSubmit = () => {
    if (uid) {
      sendAjax({
        url: `${BACKEND_URL}/api/v1/pkg/admin/providers/${uid}/`,
        method: 'put',
        data: formValues,
      }).then(
        afterSubmit
      )
    } else {
      sendAjax({
        url: `${BACKEND_URL}/api/v1/pkg/admin/providers/`,
        method: 'post',
        data: formValues,
      }).then(
        afterSubmit
      )
    }
  }

  const handleChange = (event) => {
    setFormValues({...formValues, [event.target.name]: getInputValue(event.target)})
    setFieldErrors({})
  }

  const handleAddTestLink = () => {
    setFormValues({
      ...formValues,
      test_parameters: [...formValues.test_parameters, {test_user_link: '', test_post_link: ''}]
    })
  }

  const handleRemoveTestLink = (index) => {
    const newTestParameters = formValues.test_parameters.filter((_, i) => i !== index)
    setFormValues({
      ...formValues,
      test_parameters: newTestParameters,
    })
  }

  const handleTestLinkChange = (index, field, value) => {
    const newTestParameters = [...formValues.test_parameters]
    newTestParameters[index][field] = value
    setFormValues({
      ...formValues,
      test_parameters: newTestParameters,
    })
  }

  return (
    <AdminContainer>
      <Helmet>
        <title>Providers - {BRAND_NAME}</title>
      </Helmet>

      <Box
        component={ValidatorForm}
        ref={form}
        onSubmit={handleSubmit}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} sm={12} md={4}>
            <TextValidator
              label={'Title'}
              placeholder={'Title'}
              name={'title'}
              variant={'outlined'}
              fullWidth={true}
              validators={['required']}
              errorMessages={['This field is required']}
              onChange={handleChange}
              value={formValues.title}
              {...getFieldErrorParams(fieldErrors, 'title')}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={4}>
            <TextValidator
              select
              label="Service Type"
              name={'service_type'}
              variant={'outlined'}
              fullWidth={true}
              validators={['required']}
              errorMessages={['This field is required']}
              onChange={handleChange}
              value={formValues.service_type}
              {...getFieldErrorParams(fieldErrors, 'service_type')}
              InputLabelProps={{
                shrink: true,
              }}
            >
              <MenuItem value={10}>Likes</MenuItem>
              <MenuItem value={20}>Followers</MenuItem>
              <MenuItem value={50}>Views</MenuItem>
              <MenuItem value={30}>Random Comments</MenuItem>
              <MenuItem value={40}>Custom Comments</MenuItem>
              <MenuItem value={90}>Blue-Tick Comments</MenuItem>
              <MenuItem value={100}>Shares</MenuItem>
            </TextValidator>
          </Grid>

          <Grid item xs={12} sm={12} md={4}>
            <TextValidator
              select
              label="Provider Type"
              name={'provider_type'}
              variant={'outlined'}
              fullWidth={true}
              validators={['required']}
              errorMessages={['This field is required']}
              onChange={handleChange}
              value={formValues.provider_type}
              {...getFieldErrorParams(fieldErrors, 'provider_type')}
              InputLabelProps={{
                shrink: true,
              }}
            >
              <MenuItem value={10}>SocialPanel24</MenuItem>
              <MenuItem value={20}>Peakerr</MenuItem>
              <MenuItem value={30}>SmmRaja</MenuItem>
              <MenuItem value={40}>Yarinsta.org</MenuItem>
              <MenuItem value={50}>MTP</MenuItem>
            </TextValidator>
          </Grid>

          <Grid item xs={12} sm={12} md={4}>
            <TextValidator
              label={'Service Code'}
              placeholder={'Service Code'}
              name={'service_code'}
              variant={'outlined'}
              fullWidth={true}
              validators={['required']}
              errorMessages={['This field is required']}
              onChange={handleChange}
              value={formValues.service_code}
              {...getFieldErrorParams(fieldErrors, 'service_code')}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={4}>
            <TextValidator
              label={'Min Quantity'}
              placeholder={'Min Quantity'}
              name={'min_quantity'}
              variant={'outlined'}
              fullWidth={true}
              validators={['required']}
              errorMessages={['This field is required']}
              onChange={handleChange}
              value={formValues.min_quantity}
              type={'number'}
              {...getFieldErrorParams(fieldErrors, 'min_quantity')}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={4}>
            <TextValidator
              label={'Max Quantity'}
              placeholder={'Max Quantity'}
              name={'max_quantity'}
              variant={'outlined'}
              fullWidth={true}
              validators={['required']}
              errorMessages={['This field is required']}
              onChange={handleChange}
              value={formValues.max_quantity}
              type={'number'}
              {...getFieldErrorParams(fieldErrors, 'max_quantity')}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={4}>
            <TextValidator
              select
              label="Social"
              name={'social'}
              variant={'outlined'}
              fullWidth={true}
              validators={['required']}
              errorMessages={['This field is required']}
              onChange={handleChange}
              value={formValues.social}
              {...getFieldErrorParams(fieldErrors, 'social')}
              InputLabelProps={{
                shrink: true,
              }}
            >
              <MenuItem value={10}>Instagram</MenuItem>
              <MenuItem value={20}>TikTok</MenuItem>
              <MenuItem value={30}>Clubhouse</MenuItem>
            </TextValidator>
          </Grid>

          <Grid item xs={12} sm={12} md={4}>
            <TextValidator
              select
              label="Service Status"
              name={'service_status'}
              variant={'outlined'}
              fullWidth={true}
              validators={['required']}
              errorMessages={['This field is required']}
              onChange={handleChange}
              value={formValues.service_status}
              {...getFieldErrorParams(fieldErrors, 'service_status')}
              InputLabelProps={{
                shrink: true,
              }}
            >
              <MenuItem value={10}>Available</MenuItem>
              <MenuItem value={20}>Unavailable</MenuItem>
              <MenuItem value={30}>Disabled</MenuItem>
            </TextValidator>
          </Grid>


          <Grid item xs={12} sm={12} md={4}>
            <TextValidator
              label={'Index'}
              placeholder={'Index'}
              name={'index'}
              variant={'outlined'}
              fullWidth={true}
              validators={['required']}
              errorMessages={['This field is required']}
              onChange={handleChange}
              value={formValues.index}
              type={'number'}
              {...getFieldErrorParams(fieldErrors, 'index')}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

        </Grid>

        <Divider sx={{margin: '40px 0 20px 0'}}/>

        <Box sx={{marginBottom: '20px'}}>
          <h3>Optional Information</h3>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={12} md={6}>
            <TextValidator
              label={'Starts After'}
              placeholder={'Starts After'}
              name={'starts_after'}
              variant={'outlined'}
              fullWidth={true}
              multiline
              rows={3}
              onChange={handleChange}
              value={formValues.starts_after}
              {...getFieldErrorParams(fieldErrors, 'starts_after')}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={6}>
            <TextValidator
              label={'Speed'}
              placeholder={'Speed'}
              name={'speed'}
              variant={'outlined'}
              fullWidth={true}
              multiline
              rows={3}
              onChange={handleChange}
              value={formValues.speed}
              {...getFieldErrorParams(fieldErrors, 'speed')}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={6}>
            <TextValidator
              label={'Quality'}
              placeholder={'Quality'}
              name={'quality'}
              variant={'outlined'}
              fullWidth={true}
              multiline
              rows={3}
              onChange={handleChange}
              value={formValues.quality}
              {...getFieldErrorParams(fieldErrors, 'quality')}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={6}>
            <TextValidator
              label={'Stability'}
              placeholder={'Stability'}
              name={'stability'}
              variant={'outlined'}
              fullWidth={true}
              multiline
              rows={3}
              onChange={handleChange}
              value={formValues.stability}
              {...getFieldErrorParams(fieldErrors, 'stability')}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={6}>
            <TextValidator
              label={'Drop'}
              placeholder={'Drop'}
              name={'drop'}
              variant={'outlined'}
              fullWidth={true}
              multiline
              rows={3}
              onChange={handleChange}
              value={formValues.drop}
              {...getFieldErrorParams(fieldErrors, 'drop')}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={6}>
            <TextValidator
              label={'Description'}
              placeholder={'Description'}
              name={'description'}
              variant={'outlined'}
              fullWidth={true}
              multiline
              rows={3}
              onChange={handleChange}
              value={formValues.description}
              {...getFieldErrorParams(fieldErrors, 'description')}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
        </Grid>

        <Divider sx={{margin: '40px 0 20px 0'}}/>

        <Box sx={{marginBottom: '20px'}}>
          <h3>Testing Parameters</h3>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={12} md={3}>
            <TextValidator
              select
              label="Auto Test Enabled"
              name={'auto_test_enabled'}
              variant={'outlined'}
              fullWidth={true}
              onChange={handleChange}
              value={formValues.auto_test_enabled}
              {...getFieldErrorParams(fieldErrors, 'auto_test_enabled')}
              InputLabelProps={{
                shrink: true,
              }}
            >
              <MenuItem value={true}>Yes</MenuItem>
              <MenuItem value={false}>No</MenuItem>
            </TextValidator>
          </Grid>

          <Grid item xs={12} sm={12} md={3}>
            <TextValidator
              label={'Test Every (minutes)'}
              placeholder={'Test Every (minutes)'}
              name={'test_every'}
              variant={'outlined'}
              fullWidth={true}
              type={'number'}
              onChange={handleChange}
              value={formValues.test_every}
              {...getFieldErrorParams(fieldErrors, 'test_every')}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={3}>
            <TextValidator
              label={'Complete Time (minutes)'}
              placeholder={'Complete Time (minutes)'}
              name={'complete_time'}
              variant={'outlined'}
              fullWidth={true}
              type={'number'}
              onChange={handleChange}
              value={formValues.complete_time}
              {...getFieldErrorParams(fieldErrors, 'complete_time')}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={3}>
            <TextValidator
              label={'Test Quantity'}
              placeholder={'Test Quantity'}
              name={'test_quantity'}
              variant={'outlined'}
              fullWidth={true}
              type={'number'}
              onChange={handleChange}
              value={formValues.test_quantity}
              {...getFieldErrorParams(fieldErrors, 'test_quantity')}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
              <Typography variant="body1">
                Test Links
              </Typography>

              <Button
                variant="outlined"
                startIcon={<AddIcon/>}
                onClick={handleAddTestLink}
                size="small"
              >
                Add Test Link
              </Button>
            </Box>

            {formValues.test_parameters.length > 0 ? (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>User Link</TableCell>
                      <TableCell>Post Link</TableCell>
                      <TableCell width="100">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {formValues.test_parameters.map((testParameter, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <TextValidator
                            placeholder={'User/Account Link'}
                            variant={'outlined'}
                            fullWidth={true}
                            size="small"
                            value={testParameter.test_user_link || ''}
                            onChange={(e) => handleTestLinkChange(index, 'test_user_link', e.target.value)}
                            InputLabelProps={{
                              shrink: true,
                            }}
                            inputProps={{
                              autoComplete: 'off',
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextValidator
                            placeholder={'Post Link'}
                            variant={'outlined'}
                            fullWidth={true}
                            size="small"
                            value={formValues.service_type === 20 ? '' : testParameter.test_post_link || ''}
                            onChange={(e) => handleTestLinkChange(index, 'test_post_link', e.target.value)}
                            InputLabelProps={{
                              shrink: true,
                            }}
                            inputProps={{
                              autoComplete: 'off',
                            }}
                            disabled={formValues.service_type === 20}
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            onClick={() => handleRemoveTestLink(index)}
                            color="error"
                            size="small"
                          >
                            <DeleteIcon/>
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{textAlign: 'center'}}>
                <p>No test links added. Click "Add Test Link" to add your first test link.</p>
              </Box>
            )}
          </Grid>
        </Grid>

        <Button
          color={'primary'}
          type={'submit'}
          variant="contained"
          sx={{
            margin: '20px 0 500px 0',
          }}
          onClick={() => {
          }}
        >
          Save
        </Button>
      </Box>
    </AdminContainer>
  )
}

export default ProviderCreateUpdateAdminPage
